import CourieButton from "@src/common/components/Button/Button";
import { Loading } from "@src/common/components";
import type { ManualArrivalOverrideTabProps } from "./types";
import { TaskArrivalRow } from "./components/TaskArrivalRow";
import { sortTasksBySequenceNumber } from "@src/shipments/utils/sortTasksBySequenceNumber";
import { useManualArrivalData } from "./hooks/useManualArrivalData";
import { useManualArrivalForm } from "./hooks/useManualArrivalForm";
import { useManualArrivalSubmit } from "./hooks/useManualArrivalSubmit";

function ManualArrivalOverrideTab({
  shipment,
  onClose,
}: ManualArrivalOverrideTabProps) {
  const isShipmentAssigned = Boolean(
    shipment?.driverId ?? shipment?.driver?.id,
  );

  const { sortedStops, loading: dataLoading, arrivalShipment } =
    useManualArrivalData(shipment, isShipmentAssigned);
  const { control, handleSubmit, dirtyFields } = useManualArrivalForm(
    sortedStops,
    isShipmentAssigned,
  );
  const { onSubmit, loading: submitLoading } = useManualArrivalSubmit({
    shipment,
    sortedStops,
    isShipmentAssigned,
    onClose,
    handleSubmit,
    dirtyFields,
  });

  if (!shipment) return null;

  if (!isShipmentAssigned) {
    return (
      <p className="text-sm text-gray-600 dark:text-gray-400 py-2">
        Manual arrival is only available after a driver is assigned to this
        order.
      </p>
    );
  }

  if (dataLoading && !arrivalShipment) {
    return (
      <div className="flex justify-center py-12">
        <Loading loading />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="mb-4">
        <div className="grid grid-cols-[2fr,1fr,1fr] gap-4 mb-3 pb-2 border-b border-gray-300 items-center">
          <div className="font-semibold text-sm text-gray-700 dark:text-gray-300">
            Stop / task
          </div>
          <div className="font-semibold text-sm text-gray-700 dark:text-gray-300">
            Arrival date
          </div>
          <div className="font-semibold text-sm text-gray-700 dark:text-gray-300">
            Arrival time
          </div>
        </div>

        <div className="space-y-3 max-h-[420px] overflow-y-auto">
          {sortedStops.every((s) => !(s.tasks || []).length) && (
            <p className="text-sm text-gray-500 py-2">
              No tasks found on this shipment.
            </p>
          )}
          {sortedStops.flatMap((stop) =>
            sortTasksBySequenceNumber(stop.tasks).map((task) => (
              <TaskArrivalRow
                key={task.id}
                stop={stop}
                task={task}
                control={control}
              />
            )),
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <CourieButton onClick={onClose} color="secondary" type="button">
          Cancel
        </CourieButton>
        <CourieButton
          type="submit"
          disabled={submitLoading}
          isProcessing={submitLoading}
        >
          Override arrival(s)
        </CourieButton>
      </div>
    </form>
  );
}

export { ManualArrivalOverrideTab };
export type { ManualArrivalOverrideTabProps } from "./types";
